import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ROUTE_PATHS, Announcement, AnnouncementImage } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fadeInUp } from '@/lib/motion';

export default function AnnouncementPage() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAnnouncement() {
      const { data } = await supabase
        .from('announcements_2026_04_15')
        .select('*')
        .eq('published', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();
      if (data) {
        const raw = data as {
          id: string;
          title: string;
          content: string;
          images: unknown;
          published: boolean;
          created_at: string;
          updated_at: string;
        };
        const images: AnnouncementImage[] = Array.isArray(raw.images)
          ? (raw.images as AnnouncementImage[]).sort((a, b) => a.order - b.order)
          : [];
        setAnnouncement({ ...raw, images });
      } else {
        setAnnouncement(null);
      }
      setIsLoading(false);
    }
    fetchAnnouncement();

    // Realtime: reflect admin changes instantly
    const channel = supabase
      .channel('announcement-page-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'announcements_2026_04_15' },
        () => fetchAnnouncement()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Auto-advance images every 5 seconds
  useEffect(() => {
    if (!announcement || announcement.images.length <= 1) return;
    const timer = setInterval(() => {
      setImgIndex(i => (i + 1) % announcement.images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [announcement]);

  const prevImg = () => {
    if (!announcement) return;
    setImgIndex(i => (i - 1 + announcement.images.length) % announcement.images.length);
  };
  const nextImg = () => {
    if (!announcement) return;
    setImgIndex(i => (i + 1) % announcement.images.length);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col">
      {/* Header */}
      <header className="bg-background/90 backdrop-blur-md border-b border-border px-4 py-4 sticky top-0 z-30">
        <div className="container mx-auto flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <img
              src="/images/hkcmprgu-logo-01.png"
              alt="香港中醫師權益總工會"
              className="h-10 w-auto"
            />
            <span className="text-lg font-bold text-primary hidden sm:inline">
              香港中醫師權益總工會
            </span>
          </Link>
          <Link to={ROUTE_PATHS.HOME}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              返回主頁
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl">
        {isLoading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : announcement ? (
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            {/* Title block */}
            <div className="mb-6">
              <Badge className="mb-3 bg-primary/10 text-primary border-primary/20">最新公告</Badge>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight mb-2">
                {announcement.title}
              </h1>
              <p className="text-sm text-muted-foreground">
                {new Date(announcement.updated_at).toLocaleDateString('zh-HK', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })}
              </p>
            </div>

            {/* Images */}
            {announcement.images.length > 0 && (
              <div className="relative rounded-2xl overflow-hidden mb-8 shadow-lg border border-border bg-black">
                <img
                  src={announcement.images[imgIndex]?.url}
                  alt={`公告圖片 ${imgIndex + 1}`}
                  className="w-full object-contain max-h-[70vh]"
                  style={{ display: 'block' }}
                />
                {announcement.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImg}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                      aria-label="上一張"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={nextImg}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                      aria-label="下一張"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    {/* Dot indicators */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {announcement.images.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setImgIndex(i)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            i === imgIndex ? 'bg-white scale-125' : 'bg-white/50'
                          }`}
                          aria-label={`圖片 ${i + 1}`}
                        />
                      ))}
                    </div>
                    {/* Counter */}
                    <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                      {imgIndex + 1} / {announcement.images.length}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Content */}
            <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-sm">
              <p className="text-base sm:text-lg text-foreground leading-relaxed whitespace-pre-line">
                {announcement.content}
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            className="text-center py-32"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <p className="text-muted-foreground text-lg mb-4">暫時沒有最新公告</p>
            <Link to={ROUTE_PATHS.HOME}>
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                返回主頁
              </Button>
            </Link>
          </motion.div>
        )}
      </main>

      <footer className="py-6 text-center text-sm text-muted-foreground border-t border-border">
        © 2026 香港中醫師權益總工會。保留所有權利。
      </footer>
    </div>
  );
}
